// HM-332 cluster 16: pagina /impostazioni con 7 tab SPEC mockup A
// (listini-mockup.html). Layout: page-header (title + subtitle) +
// tabs orizzontali sticky-ish + sub-route content.

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@hotel-manager/ui/components';
import {
  BedDouble,
  CalendarClock,
  Cpu,
  type LucideIcon,
  Settings2,
  Users,
  Wallet,
} from '@hotel-manager/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { listPricingRules, listRoomTypesFull } from '../../api/pricing.js';
import { fetchRooms } from '../../api/rooms.js';
import { CamereSection } from './sections/CamereSection.js';
import { EdificiSection } from './sections/EdificiSection.js';
import { FestiviSection } from './sections/FestiviSection.js';
import { HardwareSection } from './sections/HardwareSection.js';
import { HotelSection } from './sections/HotelSection.js';
import { ListiniSection } from './sections/ListiniSection.js';
import { TipologieSection } from './sections/TipologieSection.js';
import { UtentiSection } from './sections/UtentiSection.js';

// HM-373 cluster 16.4: aggiunto tab 'edifici' tra Hotel e Camere.
type TabId =
  | 'hotel'
  | 'edifici'
  | 'camere'
  | 'tipologie'
  | 'listini'
  | 'festivi'
  | 'utenti'
  | 'hardware';

interface TabSpec {
  id: TabId;
  label: string;
  icon: LucideIcon;
}

const TABS: TabSpec[] = [
  { id: 'hotel', label: 'Hotel', icon: Settings2 },
  { id: 'edifici', label: 'Edifici', icon: Settings2 },
  { id: 'camere', label: 'Camere', icon: BedDouble },
  { id: 'tipologie', label: 'Tipologie', icon: Settings2 },
  { id: 'listini', label: 'Listini', icon: Wallet },
  { id: 'festivi', label: 'Festivi', icon: CalendarClock },
  { id: 'utenti', label: 'Utenti', icon: Users },
  { id: 'hardware', label: 'Hardware', icon: Cpu },
];

export function ImpostazioniPage() {
  const [active, setActive] = useState<TabId>('listini');

  // FIX badge hardcoded (erano "14/3/3" da mockup, dati reali 28/4/10):
  // conteggi veri dalle STESSE query gia' usate dalle section (stessi
  // queryKey/queryFn) -> cache react-query condivisa, nessun doppio fetch
  // quando la section corrispondente e' montata.
  const roomsQuery = useQuery({ queryKey: ['rooms'], queryFn: fetchRooms });
  const typesQuery = useQuery({ queryKey: ['room-types'], queryFn: listRoomTypesFull });
  const rulesQuery = useQuery({ queryKey: ['pricing-rules'], queryFn: listPricingRules });

  const badges: Partial<Record<TabId, number>> = {
    camere: roomsQuery.data?.rooms.length,
    tipologie: typesQuery.data?.roomTypes.length,
    listini: rulesQuery.data?.pricingRules.length,
  };

  return (
    <div className="px-6 py-6 flex flex-col flex-1" data-testid="impostazioni-page">
      <header className="mb-5">
        <div className="text-[12px] text-text-muted flex items-center gap-1 mb-1.5">
          <Settings2 size={12} /> Impostazioni
          <span className="text-text-muted/60">›</span>
          <span>{TABS.find((t) => t.id === active)?.label ?? '—'}</span>
        </div>
        <h1 className="text-[26px] font-bold text-text-primary tracking-[-0.02em]">
          {active === 'listini'
            ? 'Listini prezzi'
            : (TABS.find((t) => t.id === active)?.label ?? 'Impostazioni')}
        </h1>
        <p className="text-[13px] text-text-secondary mt-1">
          {active === 'listini'
            ? 'Gestisci tariffe per tipologia camera con regole granulari · Listini condivisi tra tutti gli hotel della catena'
            : 'Configurazione del gestionale alberghiero'}
        </p>
      </header>

      <Tabs value={active} onValueChange={(v) => setActive(v as TabId)} className="flex-1">
        <TabsList className="border-b border-border-base mb-6">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} data-testid={`impostazioni-tab-${t.id}`}>
              <t.icon size={17} strokeWidth={1.75} />
              {t.label}
              {badges[t.id] != null && (
                <span className="ml-1.5 bg-accent-soft text-accent-primary-text text-[11px] font-semibold px-[7px] py-px rounded-full">
                  {badges[t.id]}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="hotel">
          <HotelSection />
        </TabsContent>
        <TabsContent value="edifici">
          <EdificiSection />
        </TabsContent>
        <TabsContent value="camere">
          <CamereSection />
        </TabsContent>
        <TabsContent value="tipologie">
          <TipologieSection />
        </TabsContent>
        <TabsContent value="listini">
          <ListiniSection />
        </TabsContent>
        <TabsContent value="festivi">
          <FestiviSection />
        </TabsContent>
        <TabsContent value="utenti">
          <UtentiSection />
        </TabsContent>
        <TabsContent value="hardware">
          <HardwareSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
